import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Home, Building2, MessageSquare, LogOut, Plus, Edit, Trash2, Eye, Upload, GripVertical, X, Image as ImageIcon, Settings, Youtube, Star, Rocket, Users, ShieldAlert, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { adminGetVillas, adminCreateVilla, adminUpdateVilla, adminDeleteVilla, adminGetContacts, adminMarkContactRead, adminUploadImage, adminDeleteImage, adminUpdateImageOrder, getImageUrl, adminGetSettings, adminUpdateSettings, adminUploadMainImage, adminDeleteMainImage, adminUploadPDF, adminDeletePDF, triggerPublish } from '../lib/api';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { StatusBadge } from '../components/VillaCard';
import UserManagement from '../components/UserManagement';

const VILLA_STATUSES = [
  { value: 'for_sale', label: 'For Sale' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'sold', label: 'Sold' },
  { value: 'coming_soon', label: 'Coming Soon' },
];

const AdminDashboard = () => {
  const { t, language, setLanguage } = useLanguage();
  const { user, logout, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [publishing, setPublishing] = useState(false);

  // Force English language for admin panel
  useEffect(() => {
    if (language !== 'en') {
      setLanguage('en');
    }
  }, [language, setLanguage]);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [loading, isAuthenticated, navigate]);
  
  const handlePublish = async () => {
    setPublishing(true);
    try {
      const result = await triggerPublish();
      if (result.success) {
        toast.success('Site published!', {
          description: result.message,
          duration: 5000,
        });
      } else {
        toast.error('Publish failed', {
          description: result.message,
          duration: 5000,
        });
      }
    } catch (error) {
      toast.error('Publish error', {
        description: error.response?.data?.detail || error.message,
        duration: 5000,
      });
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  // Check if user is approved or super admin
  const isSuperAdmin = user?.role === 'super_admin';
  const isApproved = user?.is_approved || isSuperAdmin;

  // Logout function - defined early for use in pending approval screen
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Show "pending approval" message for non-approved users
  if (!isApproved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-lg p-8 text-center shadow-lg">
          <ShieldAlert className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-heading text-night mb-4">Account Pending Approval</h1>
          <p className="text-gray-600 mb-6">
            Your account <span className="text-gold font-medium">{user?.email}</span> has been registered but is not yet approved.
          </p>
          <p className="text-gray-400 text-sm mb-6">
            Please contact the administrator to get access to the admin panel.
          </p>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </div>
    );
  }

  const menuItems = [
    { path: '/admin/dashboard', icon: Home, label: t('admin.dashboard') },
    { path: '/admin/villas', icon: Building2, label: t('admin.villas') },
    { path: '/admin/contacts', icon: MessageSquare, label: t('admin.contacts') },
    { path: '/admin/settings', icon: Settings, label: language === 'ru' ? 'Настройки' : 'Settings' },
    // Only show Users menu for super admin
    ...(isSuperAdmin ? [{ path: '/admin/users', icon: Users, label: 'Users' }] : []),
  ];

  return (
    <>
      <Helmet>
        <title>{`${t('admin.dashboard')} — Villa.ooo`}</title>
      </Helmet>

      <div className="min-h-screen flex" data-testid="admin-dashboard">
        {/* Sidebar */}
        <aside className="w-64 admin-sidebar flex flex-col" data-testid="admin-sidebar">
          <div className="p-4 border-b border-gray-200">
            <Link to="/" className="flex flex-col items-center justify-center">
              <img 
                src="/logo-villa.png" 
                alt="Villa.ooo" 
                className="h-20 object-contain"
              />
              <span className="text-[9px] tracking-[0.25em] uppercase text-center whitespace-nowrap -mt-5" style={{color: '#1e3a5f'}}>
                Luxury Seafront
              </span>
            </Link>
          </div>

          <nav className="flex-1 p-4">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 mb-1 transition-colors ${
                  location.pathname === item.path
                    ? 'bg-gold/10 text-gold'
                    : 'text-text-secondary hover:bg-gray-100'
                }`}
                data-testid={`admin-nav-${item.path.split('/').pop()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm">{item.label}</span>
              </Link>
            ))}
          </nav>

          <div className="p-4 border-t border-gray-200">
            {/* Publish Button */}
            <Button 
              onClick={handlePublish}
              disabled={publishing}
              className="w-full mb-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
              data-testid="publish-site-btn"
            >
              {publishing ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Publishing...
                </>
              ) : (
                <>
                  <Rocket className="w-4 h-4 mr-2" />
                  Publish Site
                </>
              )}
            </Button>
            
            <p className="text-xs text-text-secondary mb-2">{user?.email}</p>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm text-text-secondary hover:text-gold transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              {t('admin.logout')}
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 admin-content p-8 overflow-auto">
          <Routes>
            <Route path="/dashboard" element={<DashboardHome />} />
            <Route path="/villas" element={<VillasManager />} />
            <Route path="/contacts" element={<ContactsManager />} />
            <Route path="/settings" element={<SettingsManager />} />
            <Route path="/users" element={<UsersManager />} />
          </Routes>
        </main>
      </div>
    </>
  );
};

// Dashboard Home
const DashboardHome = () => {
  const { t, language } = useLanguage();
  const [stats, setStats] = useState({ villas: 0, contacts: 0 });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [villas, contacts] = await Promise.all([adminGetVillas(), adminGetContacts()]);
      setStats({ villas: villas.length, contacts: contacts.filter(c => !c.is_read).length });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  return (
    <div data-testid="admin-dashboard-home">
      <h1 className="font-heading text-3xl text-night mb-8">{t('admin.dashboard')}</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-secondary text-sm mb-1">{t('admin.villas')}</p>
              <p className="text-3xl font-medium text-night">{stats.villas}</p>
            </div>
            <Building2 className="w-10 h-10 text-gold" />
          </div>
        </div>

        <div className="bg-white p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-secondary text-sm mb-1">New Inquiries</p>
              <p className="text-3xl font-medium text-night">{stats.contacts}</p>
            </div>
            <MessageSquare className="w-10 h-10 text-gold" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Villas Manager
const VillasManager = () => {
  const { t, language } = useLanguage();
  const [villas, setVillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingVilla, setEditingVilla] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    loadVillas();
  }, []);

  const loadVillas = async () => {
    try {
      const data = await adminGetVillas();
      setVillas(data);
    } catch (error) {
      console.error('Error loading villas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete villa?')) return;
    try {
      await adminDeleteVilla(id);
      toast.success('Villa deleted successfully!');
      loadVillas();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Error deleting villa', {
        description: error.response?.data?.detail || error.message,
      });
    }
  };

  const handleStatusChange = async (villa, newStatus) => {
    try {
      await adminUpdateVilla(villa.id, { status: newStatus });
      loadVillas();
      toast.success('Status updated successfully!');
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Error updating status', {
        description: error.response?.data?.detail || error.message,
      });
    }
  };

  const handleTogglePublish = async (villa) => {
    try {
      await adminUpdateVilla(villa.id, { is_published: !villa.is_published });
      loadVillas();
      toast.success(villa.is_published ? 'Villa unpublished' : 'Villa published!');
    } catch (error) {
      console.error('Publish toggle error:', error);
      toast.error('Error updating villa', {
        description: error.response?.data?.detail || error.message,
      });
    }
  };

  const handleToggleFeatured = async (villa) => {
    try {
      await adminUpdateVilla(villa.id, { is_featured: !villa.is_featured });
      loadVillas();
      toast.success(villa.is_featured ? 'Removed from featured' : 'Added to featured!');
    } catch (error) {
      console.error('Featured toggle error:', error);
      toast.error('Error updating villa', {
        description: error.response?.data?.detail || error.message,
      });
    }
  };

  const openCreateDialog = () => {
    setEditingVilla(null);
    setIsDialogOpen(true);
  };

  const openEditDialog = (villa) => {
    setEditingVilla(villa);
    setIsDialogOpen(true);
  };

  return (
    <div data-testid="admin-villas-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl text-night">{t('admin.villas')}</h1>
        <Button onClick={openCreateDialog} className="btn-primary" data-testid="add-villa-btn">
          <Plus className="w-4 h-4 mr-2" />
          {t('admin.addVilla')}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : villas.length === 0 ? (
        <div className="text-center py-12 text-text-secondary">No villas</div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-gray-200">
              <tr>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-text-secondary">Villa</th>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-text-secondary">Price</th>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-text-secondary">Status</th>
                <th className="text-center p-4 text-xs uppercase tracking-wider text-text-secondary">Published</th>
                <th className="text-center p-4 text-xs uppercase tracking-wider text-text-secondary">Featured</th>
                <th className="text-right p-4 text-xs uppercase tracking-wider text-text-secondary">Actions</th>
              </tr>
            </thead>
            <tbody>
              {villas.map((villa) => {
                // Get main image: main_image > first exterior > first any
                const getVillaThumb = () => {
                  if (villa.main_image?.url) return getImageUrl(villa.main_image);
                  const exteriorImgs = (villa.images || []).filter(img => (img.category || 'exterior') === 'exterior');
                  if (exteriorImgs.length > 0) return getImageUrl(exteriorImgs[0]);
                  if (villa.images?.[0]) return getImageUrl(villa.images[0]);
                  return null;
                };
                const thumbUrl = getVillaThumb();
                
                return (
                <tr key={villa.id} className="border-b border-gray-100 last:border-0" data-testid={`villa-row-${villa.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {thumbUrl ? (
                        <img src={thumbUrl} alt="" className="w-12 h-12 object-cover" />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <span className="font-medium text-night block">{villa.title_en}</span>
                        <span className="text-xs text-text-secondary">{villa.images?.length || 0} images</span>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-text-secondary">
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: villa.currency, maximumFractionDigits: 0 }).format(villa.price)}
                  </td>
                  <td className="p-4">
                    <Select value={villa.status || 'for_sale'} onValueChange={(value) => handleStatusChange(villa, value)}>
                      <SelectTrigger className="w-[140px]" data-testid={`status-select-${villa.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {VILLA_STATUSES.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            <StatusBadge status={status.value} size="small" />
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4 text-center">
                    <Switch
                      checked={villa.is_published}
                      onCheckedChange={() => handleTogglePublish(villa)}
                      data-testid={`publish-switch-${villa.id}`}
                    />
                  </td>
                  <td className="p-4 text-center">
                    <Switch
                      checked={villa.is_featured}
                      onCheckedChange={() => handleToggleFeatured(villa)}
                      data-testid={`featured-switch-${villa.id}`}
                    />
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(villa)} data-testid={`edit-villa-${villa.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(villa.id)} data-testid={`delete-villa-${villa.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}

      <VillaDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        villa={editingVilla}
        onSave={loadVillas}
      />
    </div>
  );
};

// Image Categories
const IMAGE_CATEGORIES = [
  { id: 'exterior', label_en: 'Exterior', label_ru: 'Экстерьер' },
  { id: 'interior', label_en: 'Interior', label_ru: 'Интерьер' },
  { id: 'floor_plans', label_en: 'Floor Plans', label_ru: 'Планы этажей' },
  { id: 'details', label_en: 'Details', label_ru: 'Детали' },
];

// Main Image Uploader Component
const MainImageUploader = ({ villaId, mainImage, onUpdate, isReloading }) => {
  const { language } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const getImageUrl = (img) => {
    if (!img) return null;
    if (img.url && img.url.startsWith('http')) return img.url;
    if (img.url) return `${API_URL}${img.url}`;
    if (img.storage_path) return `${API_URL}${img.storage_path}`;
    return null;
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      await adminUploadMainImage(villaId, file, setUploadProgress);
      toast.success('Main image uploaded!', {
        description: 'The catalog image has been updated.',
        duration: 3000,
      });
      onUpdate();
    } catch (error) {
      toast.error('Upload failed', {
        description: error.response?.data?.detail || error.message,
        duration: 5000,
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminDeleteMainImage(villaId);
      toast.success('Main image deleted!', {
        description: 'You can upload a new catalog image.',
        duration: 3000,
      });
      onUpdate();
    } catch (error) {
      toast.error('Delete failed', {
        description: error.response?.data?.detail || error.message,
        duration: 5000,
      });
    } finally {
      setDeleting(false);
    }
  };

  const imageUrl = getImageUrl(mainImage);
  const isLoading = uploading || deleting || isReloading;

  return (
    <div className={`mb-6 p-4 border-2 border-dashed border-gold/50 bg-gold/5 transition-opacity ${isLoading ? 'opacity-70' : ''}`}>
      <label className="label-style flex items-center gap-2 mb-3">
        <Star className="w-4 h-4 text-gold" />
        {language === 'ru' ? 'ГЛАВНОЕ ФОТО (для каталога)' : 'MAIN IMAGE (for catalog)'}
        {isReloading && <span className="text-xs text-gold animate-pulse ml-2">Refreshing...</span>}
      </label>
      
      {imageUrl ? (
        <div className="relative w-48 h-32 group">
          <img 
            src={imageUrl} 
            alt="Main" 
            className={`w-full h-full object-cover rounded transition-opacity ${isLoading ? 'opacity-50' : ''}`}
          />
          {deleting && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded">
              <div className="animate-spin w-6 h-6 border-2 border-white border-t-transparent rounded-full" />
            </div>
          )}
          {!isLoading && (
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <button
                type="button"
                onClick={handleDelete}
                className="p-2 bg-red-500 text-white hover:bg-red-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <label className="cursor-pointer block w-48">
          <input
            type="file"
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
            disabled={isLoading}
          />
          <div className={`w-48 h-32 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-2 hover:border-gold transition-colors rounded ${isLoading ? 'cursor-wait' : ''}`}>
            {uploading ? (
              <>
                <div className="animate-spin w-6 h-6 border-2 border-gold border-t-transparent rounded-full" />
                <span className="text-sm text-gold">{uploadProgress}%</span>
              </>
            ) : (
              <>
                <Upload className="w-6 h-6 text-gray-400" />
                <span className="text-xs text-gray-500">
                  {language === 'ru' ? 'Загрузить' : 'Upload'}
                </span>
              </>
            )}
          </div>
        </label>
      )}
      <p className="text-xs text-text-secondary mt-2">
        {language === 'ru' 
          ? 'Это фото отображается в каталоге и на карточках' 
          : 'This image is shown in catalog and cards'}
      </p>
    </div>
  );
};

// Image Manager Component
const ImageManager = ({ villaId, images, onUpdate, isReloading }) => {
  const { language } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deletingId, setDeletingId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [draggedImageId, setDraggedImageId] = useState(null);
  const [activeCategory, setActiveCategory] = useState('exterior');
  const [uploadCategory, setUploadCategory] = useState('exterior');

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    let successCount = 0;
    let errorMessages = [];
    
    const categoryLabel = IMAGE_CATEGORIES.find(c => c.id === uploadCategory)?.label_en || uploadCategory;
    
    try {
      for (const file of files) {
        setUploadProgress(0);
        try {
          await adminUploadImage(villaId, file, uploadCategory, setUploadProgress);
          successCount++;
        } catch (err) {
          const errorMsg = err.response?.data?.detail || err.message || 'Unknown error';
          errorMessages.push(`${file.name}: ${errorMsg}`);
          console.error(`Upload error for ${file.name}:`, err);
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} image(s) uploaded!`, {
          description: `Added to ${categoryLabel} gallery.`,
          duration: 3000,
        });
      }
      if (errorMessages.length > 0) {
        errorMessages.forEach(msg => toast.error('Upload failed', { description: msg }));
      }
      onUpdate();
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (imageId) => {
    setDeletingId(imageId);
    try {
      await adminDeleteImage(villaId, imageId);
      toast.success('Image deleted!', {
        description: 'Gallery has been updated.',
        duration: 3000,
      });
      onUpdate();
    } catch (error) {
      toast.error('Delete failed', {
        description: error.response?.data?.detail || error.message,
        duration: 5000,
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }
    
    // Reorder within category
    const newCategoryImages = [...categoryImages];
    const [draggedImage] = newCategoryImages.splice(draggedIndex, 1);
    newCategoryImages.splice(dropIndex, 0, draggedImage);
    
    // Update order property for correct sorting after state update
    const reorderedCategoryImages = newCategoryImages.map((img, idx) => ({
      ...img,
      order: idx // Update order to match new position
    }));
    
    // Rebuild full images array with updated orders
    const otherImages = (images || []).filter(img => (img.category || 'exterior') !== activeCategory);
    const reorderedImages = [...otherImages, ...reorderedCategoryImages];
    
    // Update locally first for instant feedback
    onUpdate(reorderedImages);
    
    // Save to backend
    const imageIds = reorderedImages.map(img => img.id);
    setSavingOrder(true);
    try {
      await adminUpdateImageOrder(villaId, imageIds);
      toast.success('Order saved!', {
        description: 'Image positions have been updated.',
        duration: 2000,
      });
    } catch (error) {
      toast.error('Failed to save order', {
        description: error.response?.data?.detail || error.message,
      });
      // Reload to restore correct order from server
      onUpdate();
    } finally {
      setSavingOrder(false);
    }
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDraggedImageId(null);
  };

  // Filter images by active category
  const categoryImages = [...(images || [])]
    .filter(img => (img.category || 'exterior') === activeCategory)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  
  // Count images per category
  const categoryCounts = IMAGE_CATEGORIES.reduce((acc, cat) => {
    acc[cat.id] = (images || []).filter(img => (img.category || 'exterior') === cat.id).length;
    return acc;
  }, {});

  const isLoading = uploading || isReloading || savingOrder;

  return (
    <div className={`space-y-4 transition-opacity ${isLoading ? 'opacity-70' : ''}`}>
      {/* Category Tabs */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {IMAGE_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            type="button"
            onClick={() => {
              setActiveCategory(cat.id);
              setUploadCategory(cat.id);
            }}
            className={`px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${
              activeCategory === cat.id
                ? 'text-gold border-b-2 border-gold'
                : 'text-text-secondary hover:text-night'
            }`}
          >
            {language === 'ru' ? cat.label_ru : cat.label_en}
            {categoryCounts[cat.id] > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-200 rounded-full">
                {categoryCounts[cat.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Upload Button */}
      <div className="flex items-center justify-between">
        <label className="label-style flex items-center gap-2">
          {language === 'ru' ? IMAGE_CATEGORIES.find(c => c.id === activeCategory)?.label_ru : IMAGE_CATEGORIES.find(c => c.id === activeCategory)?.label_en}
          {(isReloading || savingOrder) && <span className="text-xs text-gold animate-pulse">Updating...</span>}
        </label>
        <label className={`cursor-pointer ${uploading ? 'cursor-wait' : ''}`}>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-night text-sm hover:bg-gold/80 transition-colors">
            {uploading ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-night border-t-transparent rounded-full" />
                {uploadProgress}%
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                {language === 'ru' ? 'Загрузить' : 'Upload'}
              </>
            )}
          </span>
        </label>
      </div>

      {categoryImages.length > 0 ? (
        <div className="grid grid-cols-4 gap-3">
          {categoryImages.map((image, index) => {
            const isDeleting = deletingId === image.id;
            return (
              <div
                key={image.id}
                draggable={!isDeleting}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                className={`relative group aspect-square bg-gray-100 cursor-move transition-all ${
                  draggedIndex === index ? 'opacity-50 ring-2 ring-gold scale-95' : ''
                } ${index === 0 && activeCategory === 'exterior' ? 'ring-2 ring-gold' : ''} ${isDeleting ? 'opacity-50' : ''}`}
              >
                <img
                  src={getImageUrl(image)}
                  alt={image.original_filename}
                  className="w-full h-full object-cover"
                />
                {index === 0 && activeCategory === 'exterior' && (
                  <span className="absolute top-1 left-1 bg-gold text-night text-[10px] px-1.5 py-0.5 font-medium">
                    MAIN
                  </span>
                )}
                {isDeleting ? (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="animate-spin w-6 h-6 border-2 border-white border-t-transparent rounded-full" />
                  </div>
                ) : (
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <GripVertical className="w-5 h-5 text-white" />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDelete(image.id);
                      }}
                      className="p-1 bg-red-500 text-white hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-200 p-8 text-center text-text-secondary">
          <ImageIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">
            {language === 'ru' ? 'Нет изображений в этой категории' : 'No images in this category'}
          </p>
        </div>
      )}
      
      <p className="text-xs text-text-secondary">
        {language === 'ru' 
          ? 'Перетащите для сортировки. Первое фото в "Экстерьер" — главное.'
          : 'Drag to reorder. First image in "Exterior" is the main photo.'
        }
      </p>
    </div>
  );
};

// Villa Create/Edit Dialog
const VillaDialog = ({ isOpen, onClose, villa, onSave }) => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    title_en: '', title_de: '', title_pl: '', title_sv: '', title_cs: '',
    description_en: '', description_de: '', description_pl: '', description_sv: '', description_cs: '',
    meta_description_en: '', meta_description_de: '', meta_description_pl: '', meta_description_sv: '', meta_description_cs: '',
    price: '', currency: 'EUR', price_on_request: true,
    bedrooms: '', bathrooms: '', area: '', land_area: '',
    location_en: '', location_de: '', location_pl: '', location_sv: '', location_cs: '',
    latitude: '', longitude: '',
    features_en: '', features_de: '', features_pl: '', features_sv: '', features_cs: '',
    youtube_url: '',
    pdf_presentation_url: '',
    status: 'for_sale',
    is_featured: false, is_published: true,
  });
  const [images, setImages] = useState([]);
  const [mainImage, setMainImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [activeTab, setActiveTab] = useState('en');

  useEffect(() => {
    if (villa) {
      setFormData({
        title_en: villa.title_en || '',
        title_de: villa.title_de || '',
        title_pl: villa.title_pl || '',
        title_sv: villa.title_sv || '',
        title_cs: villa.title_cs || '',
        description_en: villa.description_en || '',
        description_de: villa.description_de || '',
        description_pl: villa.description_pl || '',
        description_sv: villa.description_sv || '',
        description_cs: villa.description_cs || '',
        meta_description_en: villa.meta_description_en || '',
        meta_description_de: villa.meta_description_de || '',
        meta_description_pl: villa.meta_description_pl || '',
        meta_description_sv: villa.meta_description_sv || '',
        meta_description_cs: villa.meta_description_cs || '',
        price: villa.price?.toString() || '',
        currency: villa.currency || 'EUR',
        price_on_request: villa.price_on_request ?? true,
        bedrooms: villa.bedrooms?.toString() || '',
        bathrooms: villa.bathrooms?.toString() || '',
        area: villa.area?.toString() || '',
        land_area: villa.land_area?.toString() || '',
        location_en: villa.location_en || '',
        location_de: villa.location_de || '',
        location_pl: villa.location_pl || '',
        location_sv: villa.location_sv || '',
        location_cs: villa.location_cs || '',
        latitude: villa.latitude?.toString() || '',
        longitude: villa.longitude?.toString() || '',
        features_en: villa.features_en?.join('\n') || '',
        features_de: villa.features_de?.join('\n') || '',
        features_pl: villa.features_pl?.join('\n') || '',
        features_sv: villa.features_sv?.join('\n') || '',
        features_cs: villa.features_cs?.join('\n') || '',
        youtube_url: villa.youtube_url || '',
        pdf_presentation_url: villa.pdf_presentation_url || '',
        status: villa.status || 'for_sale',
        is_featured: villa.is_featured || false,
        is_published: villa.is_published ?? true,
      });
      setImages(villa.images || []);
      setMainImage(villa.main_image || null);
    } else {
      setFormData({
        title_en: '', title_de: '', title_pl: '', title_sv: '', title_cs: '',
        description_en: '', description_de: '', description_pl: '', description_sv: '', description_cs: '',
        meta_description_en: '', meta_description_de: '', meta_description_pl: '', meta_description_sv: '', meta_description_cs: '',
        price: '', currency: 'EUR', price_on_request: true,
        bedrooms: '', bathrooms: '', area: '', land_area: '',
        location_en: '', location_de: '', location_pl: '', location_sv: '', location_cs: '',
        latitude: '', longitude: '',
        features_en: '', features_de: '', features_pl: '', features_sv: '', features_cs: '',
        youtube_url: '',
        pdf_presentation_url: '',
        status: 'for_sale',
        is_featured: false, is_published: true,
      });
      setImages([]);
      setMainImage(null);
    }
  }, [villa, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title_en: formData.title_en,
        title_de: formData.title_de,
        title_pl: formData.title_pl,
        title_sv: formData.title_sv,
        title_cs: formData.title_cs,
        description_en: formData.description_en,
        description_de: formData.description_de,
        description_pl: formData.description_pl,
        description_sv: formData.description_sv,
        description_cs: formData.description_cs,
        meta_description_en: formData.meta_description_en,
        meta_description_de: formData.meta_description_de,
        meta_description_pl: formData.meta_description_pl,
        meta_description_sv: formData.meta_description_sv,
        meta_description_cs: formData.meta_description_cs,
        price: formData.price_on_request ? 0 : parseFloat(formData.price) || 0,
        currency: formData.currency,
        price_on_request: formData.price_on_request,
        bedrooms: parseInt(formData.bedrooms),
        bathrooms: parseInt(formData.bathrooms),
        area: parseFloat(formData.area),
        land_area: parseFloat(formData.land_area) || 0,
        location_en: formData.location_en,
        location_de: formData.location_de,
        location_pl: formData.location_pl,
        location_sv: formData.location_sv,
        location_cs: formData.location_cs,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        features_en: formData.features_en.split('\n').filter(Boolean),
        features_de: formData.features_de.split('\n').filter(Boolean),
        features_pl: formData.features_pl.split('\n').filter(Boolean),
        features_sv: formData.features_sv.split('\n').filter(Boolean),
        features_cs: formData.features_cs.split('\n').filter(Boolean),
        youtube_url: formData.youtube_url,
        pdf_presentation_url: formData.pdf_presentation_url,
        status: formData.status,
        is_featured: formData.is_featured,
        is_published: formData.is_published,
      };

      if (villa) {
        await adminUpdateVilla(villa.id, payload);
        toast.success('Villa saved successfully!', {
          description: 'All changes have been saved.',
          duration: 3000,
        });
      } else {
        await adminCreateVilla(payload);
        toast.success('Villa created successfully!', {
          description: 'New villa has been added.',
          duration: 3000,
        });
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Save error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error('Error saving villa', {
        description: errorMessage,
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  // Reload images and main_image (without closing dialog)
  const reloadVillaData = useCallback(async () => {
    if (villa) {
      setReloading(true);
      try {
        const villas = await adminGetVillas();
        const updatedVilla = villas.find(v => v.id === villa.id);
        if (updatedVilla) {
          setImages(updatedVilla.images || []);
          setMainImage(updatedVilla.main_image || null);
        }
      } catch (error) {
        console.error('Error reloading villa data:', error);
        toast.error('Error refreshing data');
      } finally {
        setReloading(false);
      }
    }
  }, [villa]);

  const handleImagesUpdate = useCallback((newImages) => {
    if (newImages) {
      setImages(newImages);
    } else {
      // Reload all image data without closing dialog
      reloadVillaData();
    }
  }, [reloadVillaData]);

  const handleMainImageUpdate = useCallback(() => {
    // Reload villa data to get updated main_image
    reloadVillaData();
  }, [reloadVillaData]);

  const tabs = [
    { id: 'en', label: 'EN' },
    { id: 'de', label: 'DE' },
    { id: 'pl', label: 'PL' },
    { id: 'sv', label: 'SV' },
    { id: 'cs', label: 'CS' },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {villa ? t('admin.editVilla') : t('admin.addVilla')}
            {reloading && <span className="ml-2 text-sm text-gold animate-pulse">Updating...</span>}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4" data-testid="villa-form">
          {/* Main Image - Only show when editing */}
          {villa && (
            <MainImageUploader 
              villaId={villa.id}
              mainImage={mainImage}
              onUpdate={handleMainImageUpdate}
              isReloading={reloading}
            />
          )}

          {/* Images - Only show when editing */}
          {villa && (
            <ImageManager 
              villaId={villa.id} 
              images={images} 
              onUpdate={handleImagesUpdate}
              isReloading={reloading}
            />
          )}

          {/* Status */}
          <div>
            <label className="label-style block mb-2">Status</label>
            <div className="flex gap-2 flex-wrap">
              {VILLA_STATUSES.map((status) => (
                <button
                  key={status.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, status: status.value })}
                  className={`px-3 py-2 border transition-colors ${
                    formData.status === status.value 
                      ? 'border-gold bg-gold/10' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <StatusBadge status={status.value} size="small" />
                </button>
              ))}
            </div>
          </div>

          {/* Language Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-gold border-b-2 border-gold'
                      : 'text-text-secondary hover:text-night'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title & Description for active language */}
          <div className="space-y-4">
            <div>
              <label className="label-style block mb-2">Title ({activeTab.toUpperCase()})</label>
              <input
                type="text"
                value={formData[`title_${activeTab}`]}
                onChange={(e) => setFormData({ ...formData, [`title_${activeTab}`]: e.target.value })}
                className="input-minimal w-full border border-gray-200 px-3"
                required={activeTab === 'en'}
                data-testid={`villa-title-${activeTab}`}
              />
            </div>
            <div>
              <label className="label-style block mb-2">Meta Description ({activeTab.toUpperCase()}) — SEO</label>
              <textarea
                value={formData[`meta_description_${activeTab}`] || ''}
                onChange={(e) => setFormData({ ...formData, [`meta_description_${activeTab}`]: e.target.value })}
                className="input-minimal w-full border border-gray-200 px-3 min-h-[60px]"
                placeholder="SEO description for search engines (max 160 chars)"
                maxLength={200}
                data-testid={`villa-meta-desc-${activeTab}`}
              />
              <p className="text-xs text-text-secondary mt-1">
                {(formData[`meta_description_${activeTab}`] || '').length}/160 characters
              </p>
            </div>
            <div>
              <label className="label-style block mb-2">Description ({activeTab.toUpperCase()})</label>
              <textarea
                value={formData[`description_${activeTab}`]}
                onChange={(e) => setFormData({ ...formData, [`description_${activeTab}`]: e.target.value })}
                className="input-minimal w-full border border-gray-200 px-3 min-h-[100px]"
                required={activeTab === 'en'}
                data-testid={`villa-desc-${activeTab}`}
              />
            </div>
            <div>
              <label className="label-style block mb-2">Location ({activeTab.toUpperCase()})</label>
              <input
                type="text"
                value={formData[`location_${activeTab}`]}
                onChange={(e) => setFormData({ ...formData, [`location_${activeTab}`]: e.target.value })}
                className="input-minimal w-full border border-gray-200 px-3"
                required={activeTab === 'en'}
                data-testid={`villa-location-${activeTab}`}
              />
            </div>
            <div>
              <label className="label-style block mb-2">Features ({activeTab.toUpperCase()}) - one per line</label>
              <textarea
                value={formData[`features_${activeTab}`]}
                onChange={(e) => setFormData({ ...formData, [`features_${activeTab}`]: e.target.value })}
                className="input-minimal w-full border border-gray-200 px-3 min-h-[80px]"
                placeholder="Pool&#10;Sea view&#10;Terrace"
                data-testid={`villa-features-${activeTab}`}
              />
            </div>
          </div>

          {/* Common Fields */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="label-style block mb-2">Price (EUR)</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="input-minimal w-full border border-gray-200 px-3"
                disabled={formData.price_on_request}
                data-testid="villa-price"
              />
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.price_on_request}
                  onChange={(e) => setFormData({ ...formData, price_on_request: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="villa-price-on-request"
                />
                <span className="text-sm text-text-secondary">On request</span>
              </label>
            </div>
            <div>
              <label className="label-style block mb-2">Bedrooms</label>
              <input
                type="number"
                value={formData.bedrooms}
                onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                className="input-minimal w-full border border-gray-200 px-3"
                required
                data-testid="villa-bedrooms"
              />
            </div>
            <div>
              <label className="label-style block mb-2">Bathrooms</label>
              <input
                type="number"
                value={formData.bathrooms}
                onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                className="input-minimal w-full border border-gray-200 px-3"
                required
                data-testid="villa-bathrooms"
              />
            </div>
            <div>
              <label className="label-style block mb-2">Area (m²)</label>
              <input
                type="number"
                value={formData.area}
                onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                className="input-minimal w-full border border-gray-200 px-3"
                required
                data-testid="villa-area"
              />
            </div>
            <div>
              <label className="label-style block mb-2">Land Area (m²)</label>
              <input
                type="number"
                value={formData.land_area}
                onChange={(e) => setFormData({ ...formData, land_area: e.target.value })}
                className="input-minimal w-full border border-gray-200 px-3"
                placeholder="0"
                data-testid="villa-land-area"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-style block mb-2">Latitude</label>
              <input
                type="text"
                value={formData.latitude}
                onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                className="input-minimal w-full border border-gray-200 px-3"
                placeholder="43.5147"
                required
                data-testid="villa-lat"
              />
            </div>
            <div>
              <label className="label-style block mb-2">Longitude</label>
              <input
                type="text"
                value={formData.longitude}
                onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                className="input-minimal w-full border border-gray-200 px-3"
                placeholder="16.4435"
                required
                data-testid="villa-lng"
              />
            </div>
          </div>

          {/* YouTube Video URL */}
          <div>
            <label className="label-style block mb-2 flex items-center gap-2">
              <Youtube className="w-4 h-4 text-red-500" />
              YouTube Video URL
            </label>
            <input
              type="text"
              value={formData.youtube_url}
              onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
              className="input-minimal w-full border border-gray-200 px-3"
              placeholder="https://www.youtube.com/watch?v=..."
              data-testid="villa-youtube-url"
            />
            <p className="text-xs text-text-secondary mt-1">
              {language === 'ru' ? 'Ссылка на YouTube видео для этой виллы (необязательно)' : 'YouTube video link for this villa (optional)'}
            </p>
          </div>

          {/* PDF Presentation Upload */}
          <div>
            <label className="label-style block mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-500" />
              PDF Presentation
            </label>
            
            {formData.pdf_presentation_url ? (
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <FileText className="w-5 h-5 text-green-600" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-green-800 truncate">PDF Uploaded</p>
                  <a 
                    href={formData.pdf_presentation_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-green-600 hover:underline truncate block"
                  >
                    {formData.pdf_presentation_url.split('/').pop()}
                  </a>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    if (villa && window.confirm('Delete PDF presentation?')) {
                      try {
                        await adminDeletePDF(villa.id);
                        setFormData({ ...formData, pdf_presentation_url: '' });
                        toast.success('PDF deleted');
                      } catch (e) {
                        toast.error('Failed to delete PDF');
                      }
                    } else {
                      setFormData({ ...formData, pdf_presentation_url: '' });
                    }
                  }}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    
                    if (file.type !== 'application/pdf') {
                      toast.error('Only PDF files allowed');
                      return;
                    }
                    
                    if (file.size > 20 * 1024 * 1024) {
                      toast.error('File too large. Max 20MB');
                      return;
                    }
                    
                    if (!villa) {
                      toast.error('Please save the villa first, then upload PDF');
                      return;
                    }
                    
                    try {
                      toast.loading('Uploading PDF...', { id: 'pdf-upload' });
                      const result = await adminUploadPDF(villa.id, file);
                      setFormData({ ...formData, pdf_presentation_url: result.pdf_url });
                      toast.success('PDF uploaded!', { id: 'pdf-upload' });
                    } catch (e) {
                      toast.error('Failed to upload PDF', { id: 'pdf-upload' });
                    }
                    
                    e.target.value = '';
                  }}
                  className="hidden"
                  id="pdf-upload-input"
                  data-testid="villa-pdf-upload"
                />
                <label htmlFor="pdf-upload-input" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">Click to upload PDF</p>
                  <p className="text-xs text-gray-400 mt-1">Max 20MB. If empty, PDF will be auto-generated.</p>
                </label>
              </div>
            )}
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
                data-testid="villa-published-switch"
              />
              <label className="text-sm">{t('admin.published')}</label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_featured}
                onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                data-testid="villa-featured-switch"
              />
              <label className="text-sm">{t('admin.featured')}</label>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              {t('admin.cancel')}
            </Button>
            <Button type="submit" disabled={saving} className="flex-1 btn-primary" data-testid="villa-save-btn">
              {saving ? '...' : t('admin.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Contacts Manager
const ContactsManager = () => {
  const { t, language } = useLanguage();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const data = await adminGetContacts();
      setContacts(data);
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await adminMarkContactRead(id);
      loadContacts();
    } catch (error) {
      toast.error('Error updating contact');
    }
  };

  return (
    <div data-testid="admin-contacts-page">
      <h1 className="font-heading text-3xl text-night mb-8">{t('admin.contacts')}</h1>

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-12 text-text-secondary">{t('admin.noContacts')}</div>
      ) : (
        <div className="space-y-4">
          {contacts.map((contact) => (
            <div
              key={contact.id}
              className={`bg-white p-6 border ${contact.is_read ? 'border-gray-200' : 'border-gold'}`}
              data-testid={`contact-item-${contact.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-medium text-night">{contact.name}</h3>
                    {!contact.is_read && (
                      <span className="text-xs bg-gold text-white px-2 py-0.5">NEW</span>
                    )}
                    {contact.user_country && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                        {contact.user_country}{contact.user_city ? `, ${contact.user_city}` : ''}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-text-secondary mb-1">{contact.email}</p>
                  {contact.phone && <p className="text-sm text-text-secondary mb-1">{contact.phone}</p>}
                  
                  {/* Villa info */}
                  {contact.villa_name && (
                    <p className="text-sm text-gold font-medium mb-2">
                      🏠 {contact.villa_name}
                    </p>
                  )}
                  
                  <p className="text-text-secondary mb-3">{contact.message}</p>
                  
                  {/* User info details */}
                  <div className="text-xs text-text-secondary bg-gray-50 p-3 rounded mt-3 space-y-1">
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {contact.user_device && (
                        <span>💻 {contact.user_device} • {contact.user_os} • {contact.user_browser}</span>
                      )}
                      {contact.user_language && (
                        <span>🌐 {contact.user_language}</span>
                      )}
                      {contact.timezone && (
                        <span>🕐 {contact.timezone}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {contact.source_page && (
                        <span>📄 {contact.source_page}</span>
                      )}
                      {contact.referrer && (
                        <span>↩️ {contact.referrer}</span>
                      )}
                    </div>
                    {(contact.utm_source || contact.utm_campaign) && (
                      <div className="text-blue-600">
                        📊 UTM: {contact.utm_source && `source=${contact.utm_source}`} 
                        {contact.utm_campaign && ` campaign=${contact.utm_campaign}`}
                        {contact.utm_medium && ` medium=${contact.utm_medium}`}
                      </div>
                    )}
                  </div>
                  
                  <p className="text-xs text-text-secondary mt-3">
                    {new Date(contact.created_at).toLocaleString('en-US')}
                    {contact.ip_address && ` • IP: ${contact.ip_address}`}
                  </p>
                </div>
                {!contact.is_read && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleMarkRead(contact.id)}
                    data-testid={`mark-read-${contact.id}`}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {t('admin.markRead')}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Settings Manager
const SettingsManager = () => {
  const { language } = useLanguage();
  const [settings, setSettings] = useState({
    youtube_url: '',
    whatsapp_number: '',
    email: '',
    // Home page SEO
    home_title_en: '', home_title_de: '', home_title_pl: '', home_title_sv: '', home_title_cs: '',
    home_meta_description_en: '', home_meta_description_de: '', home_meta_description_pl: '', home_meta_description_sv: '', home_meta_description_cs: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSeoTab, setActiveSeoTab] = useState('en');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await adminGetSettings();
      setSettings({
        youtube_url: data.youtube_url || '',
        whatsapp_number: data.whatsapp_number || '',
        email: data.email || '',
        // Home page SEO
        home_title_en: data.home_title_en || '',
        home_title_de: data.home_title_de || '',
        home_title_pl: data.home_title_pl || '',
        home_title_sv: data.home_title_sv || '',
        home_title_cs: data.home_title_cs || '',
        home_meta_description_en: data.home_meta_description_en || '',
        home_meta_description_de: data.home_meta_description_de || '',
        home_meta_description_pl: data.home_meta_description_pl || '',
        home_meta_description_sv: data.home_meta_description_sv || '',
        home_meta_description_cs: data.home_meta_description_cs || '',
      });
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminUpdateSettings(settings);
      toast.success('Settings saved successfully!', {
        description: 'All settings have been updated.',
        duration: 3000,
      });
    } catch (error) {
      console.error('Settings save error:', error);
      toast.error('Error saving settings', {
        description: error.response?.data?.detail || error.message,
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  // Convert YouTube watch URL to embed URL
  const getEmbedUrl = (url) => {
    if (!url) return '';
    // If already embed format
    if (url.includes('/embed/')) return url;
    // Convert watch?v= to embed
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (match) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
    return url;
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full mx-auto" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings-page">
      <h1 className="font-heading text-3xl text-night mb-8">
        {language === 'ru' ? 'Настройки сайта' : 'Site Settings'}
      </h1>

      <div className="bg-white border border-gray-200 p-6 space-y-6">
        {/* YouTube Video */}
        <div>
          <label className="label-style block mb-2 flex items-center gap-2">
            <Youtube className="w-5 h-5 text-red-500" />
            {language === 'ru' ? 'Главное видео на сайте (YouTube)' : 'Main Site Video (YouTube)'}
          </label>
          <input
            type="text"
            value={settings.youtube_url}
            onChange={(e) => setSettings({ ...settings, youtube_url: e.target.value })}
            className="input-minimal w-full border border-gray-200 px-3"
            placeholder="https://www.youtube.com/watch?v=..."
            data-testid="settings-youtube-url"
          />
          <p className="text-xs text-text-secondary mt-1">
            {language === 'ru' 
              ? 'Это видео отображается на главной странице в секции "Видеопрезентация"'
              : 'This video is displayed on the homepage in the "Video Presentation" section'
            }
          </p>
          
          {/* Preview */}
          {settings.youtube_url && (
            <div className="mt-4">
              <p className="text-xs text-text-secondary mb-2">
                {language === 'ru' ? 'Предпросмотр:' : 'Preview:'}
              </p>
              <div className="aspect-video max-w-md bg-gray-100">
                <iframe
                  src={getEmbedUrl(settings.youtube_url)}
                  title="Video preview"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}
        </div>

        {/* WhatsApp Number */}
        <div>
          <label className="label-style block mb-2">
            {language === 'ru' ? 'Номер WhatsApp' : 'WhatsApp Number'}
          </label>
          <input
            type="text"
            value={settings.whatsapp_number}
            onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
            className="input-minimal w-full border border-gray-200 px-3"
            placeholder="35795989569"
            data-testid="settings-whatsapp"
          />
          <p className="text-xs text-text-secondary mt-1">
            {language === 'ru' 
              ? 'Номер без + и пробелов (например: 35795989569)'
              : 'Number without + and spaces (e.g., 35795989569)'
            }
          </p>
        </div>

        {/* Email */}
        <div>
          <label className="label-style block mb-2">
            {language === 'ru' ? 'Email контакт' : 'Contact Email'}
          </label>
          <input
            type="email"
            value={settings.email}
            onChange={(e) => setSettings({ ...settings, email: e.target.value })}
            className="input-minimal w-full border border-gray-200 px-3"
            placeholder="bm@villa.ooo"
            data-testid="settings-email"
          />
        </div>

        {/* Home Page SEO Section */}
        <div className="border-t border-gray-200 pt-6 mt-6">
          <h2 className="font-heading text-xl text-night mb-4">
            Home Page SEO
          </h2>
          <p className="text-sm text-text-secondary mb-4">
            Set custom Title and Meta Description for each language version of the home page.
          </p>
          
          {/* Language Tabs for SEO */}
          <div className="border-b border-gray-200 mb-4">
            <div className="flex gap-1">
              {['en', 'de', 'pl', 'sv', 'cs'].map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setActiveSeoTab(lang)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    activeSeoTab === lang
                      ? 'text-gold border-b-2 border-gold'
                      : 'text-text-secondary hover:text-night'
                  }`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Title and Meta Description for active language */}
          <div className="space-y-4">
            <div>
              <label className="label-style block mb-2">
                Home Title ({activeSeoTab.toUpperCase()})
              </label>
              <input
                type="text"
                value={settings[`home_title_${activeSeoTab}`] || ''}
                onChange={(e) => setSettings({ ...settings, [`home_title_${activeSeoTab}`]: e.target.value })}
                className="input-minimal w-full border border-gray-200 px-3"
                placeholder="Buy Seafront Villas in Croatia | Luxury Villas in Dalmatia"
                data-testid={`settings-home-title-${activeSeoTab}`}
              />
            </div>
            <div>
              <label className="label-style block mb-2">
                Home Meta Description ({activeSeoTab.toUpperCase()})
              </label>
              <textarea
                value={settings[`home_meta_description_${activeSeoTab}`] || ''}
                onChange={(e) => setSettings({ ...settings, [`home_meta_description_${activeSeoTab}`]: e.target.value })}
                className="input-minimal w-full border border-gray-200 px-3 min-h-[80px]"
                placeholder="SEO description for search engines (max 160 chars)"
                maxLength={200}
                data-testid={`settings-home-meta-${activeSeoTab}`}
              />
              <p className="text-xs text-text-secondary mt-1">
                {(settings[`home_meta_description_${activeSeoTab}`] || '').length}/160 characters
              </p>
            </div>
          </div>
        </div>

        <Button 
          onClick={handleSave} 
          disabled={saving} 
          className="btn-primary"
          data-testid="settings-save-btn"
        >
          {saving ? '...' : (language === 'ru' ? 'Сохранить настройки' : 'Save Settings')}
        </Button>
      </div>
    </div>
  );
};

// Users Manager (Super Admin Only)
const UsersManager = () => {
  const { token, user } = useAuth();
  
  // Only super admin can access this page
  if (user?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <ShieldAlert className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-night mb-2">Access Denied</h2>
          <p className="text-text-secondary">Only super admin can manage users.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <h1 className="text-2xl font-heading text-night mb-8">User Management</h1>
      <UserManagement token={token} />
    </div>
  );
};

export default AdminDashboard;
